import { CategoryRounded, Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Scrollbar } from 'app/components';
import { MapLeafletGeofenceForm } from 'app/pages/Map';
import { useAsyncAction } from 'app/services';
import { geofenceCreate, geofenceDelete, geofenceUpdate } from 'app/store/map';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const MapLeafletGeofenceDialog = (props) => {
  const formRef = useRef();
  const { geofenceUpdatedMode, geofenceUpdatedItem } = useSelector((state) => state.map);
  const { open, onClose } = props;

  const {
    asyncAction: createSubmit,
    isLoading: isCreateLoading,
    isSuccessful: isCreateSuccessful,
  } = useAsyncAction(geofenceCreate, [open]);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(geofenceUpdate, [open]);
  const {
    asyncAction: deleteSubmit,
    isLoading: isDeleteLoading,
    isSuccessful: isDeleteSuccessful,
    isError: isDeleteError,
  } = useAsyncAction(geofenceDelete, [open]);

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (params) => {
    if (geofenceUpdatedMode === 'create') {
      createSubmit(params);
    } else if (geofenceUpdatedMode === 'update') {
      updateSubmit(geofenceUpdatedItem.id, params);
    }
  };

  const handleDeleteClick = () => {
    deleteSubmit(geofenceUpdatedItem.id);
  };

  useEffect(() => {
    if (isCreateSuccessful || isUpdateSuccessful || isDeleteSuccessful) {
      onClose();
    }
  }, [isCreateSuccessful, isUpdateSuccessful, isDeleteSuccessful]);

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.MuiDialogTitle-root': {
          display: 'flex',
          padding: theme.spacing(2.5),
          alignItems: 'center',
        },
        '.MuiDialogTitle-root .MuiTypography-root': {
          marginLeft: theme.spacing(1.5),
        },
        '.MuiDialogTitle-root .MuiButtonBase-root': {
          marginLeft: 'auto',
        },
        '.MuiDialogContent-root': {
          padding: theme.spacing(0),
        },
        '.MuiDialogContent-root .simplebar-container': {
          padding: theme.spacing(0, 2.5),
        },
        '.MuiDialogActions-root': {
          padding: theme.spacing(2.5),
          justifyContent: 'flex-end',
        },
        '.MuiAvatar-circular': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          'path, circle': {
            fill: theme.palette.primary.main,
          },
        },
        '.MuiAlert-root': {
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(2),
          borderRadius: theme.shape.borderRadius,
        },
      })}>
      <DialogTitle>
        <Avatar>
          <CategoryRounded fontSize='small' />
        </Avatar>
        {geofenceUpdatedMode === 'create' && <Typography component='p'>Создать геозону</Typography>}
        {geofenceUpdatedMode === 'update' && <Typography component='p'>Редактировать геозону</Typography>}
        {geofenceUpdatedMode === 'delete' && <Typography component='p'>Удалить геозону</Typography>}
        {geofenceUpdatedMode !== 'delete' && (
          <IconButton color='default' onClick={onClose}>
            <Close fontSize='small' />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Scrollbar>
          {geofenceUpdatedMode === 'delete' && (
            <Typography variant='caption'>
              Вы собираетесь удалить геозону, но это действие необратимо. Пожалуйста, будьте уверены, что Вы хотите это сделать.
            </Typography>
          )}
          {geofenceUpdatedMode !== 'delete' && (
            <MapLeafletGeofenceForm ref={formRef} defaultValues={geofenceUpdatedItem} onSubmit={handleFormSubmit} />
          )}
          {!isUpdateLoading && isUpdateError && (
            <Alert severity='error'>Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.</Alert>
          )}
          {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
          {!isDeleteLoading && isDeleteError && (
            <Alert severity='error'>Произошла ошибка при удалении данных. Попробуйте снова или обратитесь в техническую поддержку.</Alert>
          )}
        </Scrollbar>
      </DialogContent>
      <DialogActions>
        {geofenceUpdatedMode === 'create' && (
          <LoadingButton variant='contained' loading={isCreateLoading} onClick={handleFormClick} disableElevation>
            <Typography component='p'>Добавить</Typography>
          </LoadingButton>
        )}
        {geofenceUpdatedMode === 'update' && (
          <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
            <Typography component='p'>Сохранить изменения</Typography>
          </LoadingButton>
        )}
        {geofenceUpdatedMode === 'delete' && (
          <React.Fragment>
            <LoadingButton color='error' variant='contained' onClick={handleDeleteClick} loading={isDeleteLoading} disableElevation>
              Удалить
            </LoadingButton>
            <LoadingButton variant='contained' loading={isDeleteLoading} onClick={onClose} disableElevation>
              <Typography component='p'>Отмена</Typography>
            </LoadingButton>
          </React.Fragment>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MapLeafletGeofenceDialog;
